import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { getSession, changePassword } from '../lib/auth';
import { supabase, PROFILES_TABLE } from '../lib/supabase';

const Field = ({ label, value, icon }) => (
  <div className="bg-gray-50 border border-gray-200 rounded-xl px-4 py-3 flex items-start gap-3">
    <div className="w-9 h-9 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5">
      <SafeIcon icon={icon} className="text-blue-600 text-sm" />
    </div>
    <div className="min-w-0">
      <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">{label}</p>
      <p className="text-sm font-bold text-gray-800 mt-0.5 break-all">{value || '—'}</p>
    </div>
  </div>
);

const PasswordInput = ({ label, value, onChange, show, onToggle, placeholder, required }) => (
  <div>
    <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider block mb-1">
      {label}
    </label>
    <div className="relative">
      <input
        type={show ? 'text' : 'password'}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        required={required}
        className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2.5 pr-10 focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
      />
      <button
        type="button"
        onClick={onToggle}
        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
      >
        <SafeIcon icon={show ? FiIcons.FiEyeOff : FiIcons.FiEye} className="text-sm" />
      </button>
    </div>
  </div>
);

const ChangePasswordSection = () => {
  const [open, setOpen]               = useState(false);
  const [current, setCurrent]         = useState('');
  const [newPass, setNewPass]         = useState('');
  const [confirm, setConfirm]         = useState('');
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew]         = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [loading, setLoading]         = useState(false);
  const [success, setSuccess]         = useState('');
  const [error, setError]             = useState('');

  const reset = () => {
    setCurrent(''); setNewPass(''); setConfirm('');
    setShowCurrent(false); setShowNew(false); setShowConfirm(false);
    setError(''); setSuccess(''); setLoading(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(''); setSuccess('');

    if (!current) {
      setError('Please enter your current password.'); return;
    }
    if (newPass.length < 8) {
      setError('New password must be at least 8 characters.'); return;
    }
    if (newPass === current) {
      setError('New password must be different from your current password.'); return;
    }
    if (newPass !== confirm) {
      setError('New passwords do not match.'); return;
    }

    setLoading(true);
    const { error: err } = await changePassword(current, newPass);
    setLoading(false);

    if (err) {
      setError(err.message || 'Failed to update password.');
    } else {
      setSuccess('Password updated successfully!');
      reset();
      setOpen(false);
    }
  };

  return (
    <div className="px-6 pb-6">
      <button
        onClick={() => { setOpen(o => !o); reset(); }}
        className="flex items-center gap-2 text-sm font-semibold text-blue-600 hover:text-blue-700 transition-colors"
      >
        <SafeIcon icon={FiIcons.FiLock} />
        {open ? 'Cancel' : 'Change Password'}
        <SafeIcon icon={open ? FiIcons.FiChevronUp : FiIcons.FiChevronDown} className="text-xs" />
      </button>

      <AnimatePresence>
        {open && (
          <motion.form
            key="pw-form"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.25 }}
            onSubmit={handleSubmit}
            className="overflow-hidden"
          >
            <div className="mt-4 space-y-3 bg-gray-50 border border-gray-200 rounded-xl p-4">

              {/* Current Password */}
              <PasswordInput
                label="Current Password"
                value={current}
                onChange={e => setCurrent(e.target.value)}
                show={showCurrent}
                onToggle={() => setShowCurrent(v => !v)}
                placeholder="Enter your current password"
                required
              />

              {/* Divider */}
              <div className="border-t border-gray-200 pt-1" />

              {/* New Password */}
              <PasswordInput
                label="New Password"
                value={newPass}
                onChange={e => setNewPass(e.target.value)}
                show={showNew}
                onToggle={() => setShowNew(v => !v)}
                placeholder="Min. 8 characters"
                required
              />

              {/* Confirm New Password */}
              <PasswordInput
                label="Confirm New Password"
                value={confirm}
                onChange={e => setConfirm(e.target.value)}
                show={showConfirm}
                onToggle={() => setShowConfirm(v => !v)}
                placeholder="Re-enter new password"
                required
              />

              {/* Error / Success */}
              {error && (
                <div className="flex items-center gap-2 text-xs text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
                  <SafeIcon icon={FiIcons.FiAlertCircle} className="flex-shrink-0" />
                  {error}
                </div>
              )}
              {success && (
                <div className="flex items-center gap-2 text-xs text-green-700 bg-green-50 border border-green-200 rounded-lg px-3 py-2">
                  <SafeIcon icon={FiIcons.FiCheckCircle} className="flex-shrink-0" />
                  {success}
                </div>
              )}

              <button
                type="submit"
                disabled={loading}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white text-sm font-bold py-2.5 rounded-lg transition-all flex items-center justify-center gap-2 disabled:opacity-60"
              >
                {loading
                  ? <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  : <><SafeIcon icon={FiIcons.FiSave} /> Update Password</>
                }
              </button>
            </div>
          </motion.form>
        )}
      </AnimatePresence>
    </div>
  );
};

const ProfilePage = () => {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      const { data } = await getSession();
      const user = data?.session?.user;
      if (!user) { setLoading(false); return; }

      const { data: prof } = await supabase
        .from(PROFILES_TABLE)
        .select('*')
        .eq('id', user.id)
        .single();

      setProfile({ ...prof, authEmail: user.email });
      setLoading(false);
    };
    load();
  }, []);

  if (loading) return (
    <div className="flex items-center justify-center py-20">
      <div className="w-10 h-10 border-4 border-blue-600/30 border-t-blue-600 rounded-full animate-spin" />
    </div>
  );

  const initials = (profile?.full_name || profile?.authEmail || 'U')
    .split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2);

  return (
    <div className="max-w-2xl mx-auto px-4 py-8">
      <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
        className="bg-white rounded-2xl shadow-lg overflow-hidden">

        {/* Banner */}
        <div className="bg-gradient-to-r from-blue-600 to-blue-500 px-6 py-8 text-white">
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center text-2xl font-bold">
              {initials}
            </div>
            <div>
              <h1 className="text-xl font-bold">{profile?.full_name || 'User'}</h1>
              <p className="text-blue-100 text-sm">{profile?.authEmail}</p>
            </div>
          </div>
          <div className="mt-4 inline-flex items-center gap-2 bg-white/15 rounded-full px-3 py-1.5 text-xs font-semibold">
            <SafeIcon icon={FiIcons.FiZap} className="text-yellow-300" />
            {profile?.credits ?? 0} credits remaining
          </div>
        </div>

        {/* Read-only notice */}
        <div className="mx-6 mt-4 bg-amber-50 border border-amber-200 rounded-xl px-4 py-2.5 flex items-center gap-2">
          <SafeIcon icon={FiIcons.FiInfo} className="text-amber-500 flex-shrink-0" />
          <p className="text-xs text-amber-700 font-medium">
            Profile information is read-only. Contact support to make changes.
          </p>
        </div>

        {/* Fields */}
        <div className="p-6 grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Field label="Full Name"      value={profile?.full_name}       icon={FiIcons.FiUser} />
          <Field label="Email"          value={profile?.authEmail}       icon={FiIcons.FiMail} />
          <Field label="Mobile"         value={profile?.mobile}          icon={FiIcons.FiPhone} />
          <Field label="Country"        value={profile?.country}         icon={FiIcons.FiGlobe} />
          <Field label="Monthly Volume" value={profile?.monthly_volume}  icon={FiIcons.FiTrendingUp} />
          <Field label="Member Since"   value={profile?.created_at
            ? new Date(profile.created_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })
            : '—'} icon={FiIcons.FiCalendar} />
        </div>

        {/* Platforms */}
        {profile?.platforms?.length > 0 && (
          <div className="px-6 pb-4">
            <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">Selling Platforms</p>
            <div className="flex flex-wrap gap-2">
              {profile.platforms.map(p => (
                <span key={p} className="bg-blue-50 text-blue-700 border border-blue-200 text-xs font-semibold px-3 py-1 rounded-full">
                  {p}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Divider */}
        <div className="mx-6 border-t border-gray-100 mb-4" />

        {/* Change Password */}
        <ChangePasswordSection />
      </motion.div>
    </div>
  );
};

export default ProfilePage;